import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Menu, Stethoscope, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/atoms/Logo";
import { Badge } from "@/components/atoms/Badge";
import { LanguageSwitcher } from "@/components/molecules/LanguageSwitcher";
import { ThemeToggle } from "@/components/molecules/ThemeToggle";
import { PortalSwitcher } from "@/components/organisms/PortalSwitcher";
import { UserMenu } from "@/components/organisms/UserMenu";
import { ACCOUNT_NAV, PORTAL_CONFIG, type NavItem } from "@/app/router/portalConfig";
import { useAuth } from "@/features/auth/AuthProvider";
import { cn } from "@/lib/utils";

function NavSection({ title, items, onNavigate }: { title: string; items: NavItem[]; onNavigate: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">{title}</p>
      {items.map(({ to, labelKey, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
              isActive && "bg-primary-soft text-primary hover:bg-primary-soft hover:text-primary",
            )
          }
        >
          <Icon className="h-[18px] w-[18px]" aria-hidden />
          {t(labelKey)}
        </NavLink>
      ))}
    </div>
  );
}

/** Practice owner from the build env; the sidebar block is hidden when unset. */
const OWNER_NAME = import.meta.env.VITE_APP_OWNER_NAME?.trim() ?? "";

/** Application shell for every portal: sidebar, "Cure+ Clinics" header, content. */
export function PortalLayout() {
  const { t } = useTranslation();
  const { me, hasPermission } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const portal = me?.activePortal ?? "PATIENT";
  const config = PORTAL_CONFIG[portal];

  const sidebar = (
    <nav aria-label={t("nav.main")} className="flex h-full flex-col gap-6 p-4">
      <div className="px-2 pt-1">
        <div className="flex items-center justify-between">
          <Logo />
          <button type="button" className="rounded-lg p-2 hover:bg-muted lg:hidden" onClick={() => setMobileOpen(false)} aria-label={t("nav.close")}>
            <X className="h-5 w-5" />
          </button>
        </div>
        {OWNER_NAME && (
          <div className="mt-4 flex items-center gap-3 rounded-xl border border-primary/15 bg-primary-soft/60 px-3 py-2.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-card text-primary shadow-soft" aria-hidden>
              <Stethoscope className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-tight text-foreground" title={OWNER_NAME}>{OWNER_NAME}</p>
              <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t("nav.owner")}</p>
            </div>
          </div>
        )}
      </div>
      <NavSection title={t(config.labelKey)} items={config.nav.filter((item) => !item.permission || hasPermission(item.permission))} onNavigate={() => setMobileOpen(false)} />
      <NavSection title={t("nav.account")} items={ACCOUNT_NAV} onNavigate={() => setMobileOpen(false)} />
    </nav>
  );

  return (
    <div className="min-h-dvh bg-background">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-card focus:px-4 focus:py-2 focus:shadow-lifted">
        {t("nav.skipToContent")}
      </a>

      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r bg-card/60 lg:block">{sidebar}</aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label={t("nav.main")}>
          <div className="absolute inset-0 animate-fade-in bg-slate-950/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} aria-hidden />
          <aside className="absolute inset-y-0 left-0 w-72 animate-fade-in-up bg-card shadow-lifted">{sidebar}</aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="glass sticky top-0 z-20 border-b">
          <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
            <button type="button" className="rounded-lg p-2 hover:bg-muted lg:hidden" onClick={() => setMobileOpen(true)} aria-label={t("nav.open")}>
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex min-w-0 items-center gap-3">
              <span className="truncate text-base font-bold tracking-tight sm:text-lg">Cure+ Clinics</span>
              <Badge tone="primary" className="hidden sm:inline-flex">
                {t(config.labelKey)}
              </Badge>
            </div>
            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              <PortalSwitcher className="hidden md:inline-flex" />
              <LanguageSwitcher />
              <ThemeToggle className="hidden sm:inline-flex" />
              <UserMenu />
            </div>
          </div>
          <PortalSwitcher className="mx-4 mb-3 md:hidden" />
        </header>

        <main id="main" tabIndex={-1} className="mx-auto w-full max-w-6xl px-4 py-6 focus:outline-none sm:px-6 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
